import {
  Autocomplete,
  Button,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useEffect } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useSearchParams } from "react-router-dom";

interface PerformanceDayBrandsFilterProps {
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  setShow: any;
  setDisplay: any;
  competitorlisting: any;
  setBrandName: any;
  tableHeadings: any;
  sortedRows: any;
  brandName: any;
}

const PerformanceDayBrandsFilter = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  setShow,
  setDisplay,
  competitorlisting,
  setBrandName,
  tableHeadings,
  sortedRows,
  brandName,
}: PerformanceDayBrandsFilterProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const calculateStartAndEndDate = (selectedPeriod: string) => {
    const [month, year] = selectedPeriod.split("/").map(Number);
    const start = dayjs(`${year}-${month}-01`).format("YYYY-MM-DD"); // First day of the month
    const end = dayjs(start).endOf("month").format("YYYY-MM-DD"); // Last day of the month
    setStartDate(start);
    setEndDate(end);
  };

  const handleShow = () => {
    if (startDate && endDate) {
      setShow(true);
      setDisplay(true);

      // Update searchParams
      const updatedParams = new URLSearchParams(searchParams.toString());
      updatedParams.set("startDateTab2", startDate);
      updatedParams.set("endDateTab2", endDate);
      setSearchParams(updatedParams);
    }
  };

  useEffect(() => {
    // Read search params on mount
    const params = new URLSearchParams(searchParams);
    const startDateParam = params.get("startDateTab2");
    const endDateParam = params.get("endDateTab2");
    const brandNameParam = params.get("brandNameTab2");

    if (startDateParam) setStartDate(startDateParam);
    if (endDateParam) setEndDate(endDateParam);
    if (brandNameParam) setBrandName(brandNameParam);

    // If startDate exists, calculate endDate based on period
    if (startDateParam && endDateParam) {
      setShow(true);
    } else {
      const currentMonth = dayjs().format("MM/YYYY");
      calculateStartAndEndDate(currentMonth);
    }
    // eslint-disable-next-line
  }, []); // Run only on mount

  const filteredCompetitorListing = Array.from(new Set(competitorlisting));

  const headCells = Object.entries(tableHeadings);

  const handleExportCSV = () => {
    const csvRows = [];

    // Extract headers, excluding undesired keys (if any)
    const headers = headCells.map(([_, value]) =>
      value && typeof value === "string" ? value : ""
    );
    csvRows.push(headers.join(","));

    // Extract the table data
    sortedRows.forEach((row: any) => {
      const rowData = headCells.map(([key]) => {
        if (key === "date_of_aquisition") {
          return row[key] || "N/A";
        } else if (key === "brand_name") {
          return row[key] || "N/A";
        } else if (
          key === "clicks" ||
          key === "registrations" ||
          key === "ftd"
        ) {
          return row[key] || "0";
        } else if (key === "cost") {
          return row[key] ? `"${row[key]}$"` : "0";
        }
        return row[key] || "0";
      });
      csvRows.push(rowData.join(","));
    });

    // Convert CSV rows to a single string
    const csvString = csvRows.join("\n");

    // Create a blob and download the file
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "performance_by_day_and_brands.csv");
  };

  return (
    <>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]} sx={{ py: 2 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                    color: "secondary",
                    required: true,
                  },
                }}
                sx={{ width: "100%" }}
                value={startDate ? dayjs(startDate) : null}
                label="Start Date"
                onChange={(newValue) => {
                  if (newValue) {
                    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                    setStartDate(formattedDate);
                    setEndDate(null);
                  }
                }}
                format="DD/MM/YY"
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={2}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]} sx={{ py: 2 }}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                    color: "secondary",
                    required: true,
                  },
                }}
                sx={{ width: "100%" }}
                label="End Date"
                disabled={!startDate}
                value={endDate ? dayjs(endDate) : null}
                minDate={startDate ? dayjs(startDate) : undefined}
                onChange={(newValue) => {
                  if (newValue) {
                    const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
                    setEndDate(formattedDate);
                  }
                }}
                format="DD/MM/YY"
              />
            </DemoContainer>
          </LocalizationProvider>
        </Grid>
        <Grid item xs={5}>
          <Autocomplete
            sx={{ width: "100%", py: 2 }}
            options={filteredCompetitorListing}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search Brand or choose from the list"
                label="Competitor (optional)"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
              />
            )}
            onChange={(_, newValue) => {
              setBrandName(newValue ? newValue : []);
              const updatedParams = new URLSearchParams(searchParams);
              if (newValue) {
                updatedParams.set("brandNameTab2", newValue);
              } else {
                updatedParams.delete("brandNameTab2"); // Remove param when cleared
                updatedParams.set("pagePerformanceByDayBrand", "0");
              }
              setSearchParams(updatedParams);
            }}
            value={brandName}
            size="small"
          />
        </Grid>
        <Grid item xs={3} textAlign={"right"}>
          <Button
            color={"secondary"}
            sx={{ mr: 2 }}
            onClick={handleExportCSV}
            data-tracker="190"
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleShow}
            disabled={!endDate || !startDate}
            data-tracker="189"
          >
            Show
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default PerformanceDayBrandsFilter;
